import React, { useEffect, useState } from 'react';
import { ChipCondition } from '@/types/gql-op-types';
import { CHIP_REMOVAL_REASON_OPTIONS } from '@/lib/chipConditions';
import { Chip } from '../../lib/types';
import Modal, { ModalTitle, ModalSubtitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalSelect } from '../../components/FormField/PortalSelect';

interface ChangeConditionModalProps {
  open: boolean;
  onClose: () => void;
  chip: Chip | null;
  onConfirm: (chip: Chip, condition: ChipCondition) => Promise<void> | void;
}

export default function ChangeConditionModal({ open, onClose, chip, onConfirm }: ChangeConditionModalProps) {
  const [condition, setCondition] = useState<ChipCondition | ''>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setCondition('');
      setSubmitting(false);
    }
  }, [open]);

  if (!chip) {
    return null;
  }

  async function handleConfirm() {
    if (!condition) {
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(chip!, condition);
      onClose();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={submitting ? () => undefined : onClose}>
      <ModalTitle>Update Chip Condition</ModalTitle>
      <ModalSubtitle>
        Mark chip <strong>{chip.id}</strong>
        {chip.petName ? ` (${chip.petName})` : ''} as no longer working. This <strong>cannot be undone</strong> — once
        changed, the chip will no longer appear on your chips page.
      </ModalSubtitle>
      <PortalField label="Reason">
        <PortalSelect
          value={condition}
          onChange={(e) => setCondition(e.target.value as ChipCondition)}
          disabled={submitting}
        >
          <option value="" disabled>
            Select a reason…
          </option>
          {CHIP_REMOVAL_REASON_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </PortalSelect>
      </PortalField>
      <ModalActions>
        <Button variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleConfirm} disabled={!condition || submitting}>
          {submitting ? 'Updating…' : 'Confirm'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
